import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCompanySettings, getDefrevData, getGLLineMappings, savePaySchedule } from '../api';
import { accruedLeafKeysFromMeta, payScheduleFromSettings, validatePaySchedule, PAY_FREQUENCIES, PAYROLL_BOOKS, PAYROLL_ACCRUAL_COVERS, PAY_LAG_DAYS_MIN, PAY_LAG_DAYS_MAX, PAY_SCHEDULE_COLUMNS } from '../engines/paySchedule';
import { overlayLeafLabels } from '../utils/leafLabels';
import { ACCOUNT_REQUIRED_MESSAGE, ACCRUAL_COVERS_LABELS, FIRST_CHECK_HINT, NO_ACCRUED_LINE_MESSAGE, PAY_FREQUENCY_LABELS, PAYROLL_BOOKS_LABELS, WHEN_PAID_STATEMENT,
  payPeriodLine, payScheduleSummary, previewPayPeriods } from '../utils/payScheduleDisplay';

/* PaySchedulePanel -- THE STEP-5a "PAY SCHEDULE" PANEL (bi-weekly payroll, step 4, 2026-10-08).
 *
 * The ONLY writer of the company's pay schedule (Brian Q7: wizard-only; the server route is super-admin-only). Asked at
 * Step 5a, ABOVE the roster upload (Q14) -- before the Step-5 import, which computes and stores every salary series, and
 * after Step 4, which creates the balance-sheet lines the account picker reads.
 *
 * WHAT IT MUST NOT DO (Brian B2; CLAUDE.md 12.5 rule 92): pre-select a frequency, suggest an account by its name, or guess
 * any answer. A company that was never asked opens with every field blank.
 *
 * WHERE EACH RULE LIVES (this file owns none of them):
 *   what is required when, and every refusal's words   engines/paySchedule.js validatePaySchedule (wall mirror M15 of the
 *                                                       server's) -- shown here BEFORE the request; the server stays the judge
 *   which accounts may carry the accrual (A3)           engines/paySchedule.js accruedLeafKeysFromMeta -- the STORED Step-4
 *                                                       role 'accrued', never a name test
 *   the six-period preview (Q12)                        utils/payScheduleDisplay.js, on engines/paySchedule.js payPeriod
 *   an account's label                                  its owner, gl_line_mappings.display_name (utils/leafLabels.js)
 *
 * `onStored(row | null)` tells the wizard what is STORED (after the load, and after every save): the roster upload stays
 * disabled until a pay frequency is stored. null = the stored answer could not be read.
 * Plain ASCII on purpose (rule 55). */

const FONT = "'DM Sans',-apple-system,sans-serif";
const MONO = "'Space Mono',monospace";
const label = { fontSize: 13, fontFamily: FONT, color: '#1A1C2E', fontWeight: 500, display: 'block', marginBottom: 6 };
const field = { padding: '8px 12px', borderRadius: 6, border: '1px solid #D0D5DD', fontSize: 14, fontFamily: FONT, minWidth: 220, background: '#fff', color: '#1A1C2E' };
const hint = { fontSize: 12, color: '#888', fontFamily: FONT, marginTop: 4 };
const row = { marginBottom: 14 };
const note = { fontSize: 12, color: '#555', fontFamily: FONT, lineHeight: 1.5 };

const BLANK = { frequency: '', firstCheck: '', lag: '', books: '', covers: '', account: '' };
const draftOf = stored => ({
  frequency: (stored && stored.pay_frequency) || '',
  firstCheck: (stored && stored.pay_first_check_date) || '',
  lag: stored && stored.pay_lag_days !== null && stored.pay_lag_days !== undefined ? String(stored.pay_lag_days) : '',
  books: (stored && stored.payroll_books) || '',
  covers: (stored && stored.payroll_accrual_covers) || '',
  account: (stored && stored.accrued_payroll_bs_line_key) || '',
});
const storedOf = data => Object.fromEntries(PAY_SCHEDULE_COLUMNS.map(k => [k, data && data[k] !== undefined ? data[k] : null]));
// The lag as the route wants it: a number. A blank is "not given"; anything else is handed over for the rule to judge.
const lagValue = s => (String(s).trim() === '' ? null : Number(s));

/* THE REQUEST BODY -- the WHOLE answer; a field that does not apply is OMITTED (so a semi-monthly company sends
   { pay_frequency: 'semi_monthly' } and nothing else). A field that applies and is blank is sent as null, which the rule
   answers with its "required" message. */
export function payScheduleBody(draft) {
  const body = { pay_frequency: draft.frequency || null };
  if (draft.frequency !== 'biweekly') return body;
  body.pay_first_check_date = draft.firstCheck || null;
  body.pay_lag_days = lagValue(draft.lag);
  body.payroll_books = draft.books || null;
  if (draft.books === 'accrues') {
    body.payroll_accrual_covers = draft.covers || null;
    body.accrued_payroll_bs_line_key = draft.account || null;
  }
  return body;
}

export default function PaySchedulePanel({ companyId, closeMonth = null, onStored }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [stored, setStored] = useState(null);      // the six stored values (settings names), once read
  const [meta, setMeta] = useState({});            // bs_actuals_meta, as stored (the picker's list is judged on THIS)
  const [labels, setLabels] = useState({});        // the same, with each label from its owner
  const [draft, setDraft] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);      // the last save's { relinked, message }
  const onStoredRef = useRef(onStored);            // the parent's callback, read at the moment of use (it is not a reason to re-load)
  onStoredRef.current = onStored;
  const tell = useCallback(st => { if (onStoredRef.current) onStoredRef.current(st); }, []);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setLoadError(''); setError(''); setResult(null);
    try {
      const [sRes, dRes, gRes] = await Promise.all([
        getCompanySettings(companyId),
        getDefrevData(companyId),
        getGLLineMappings(companyId).catch(() => ({ data: [] })),   // no owner row -> the cached label (leafLabels: the mid-wizard fallback)
      ]);
      const st = storedOf(sRes.data || {});
      const m = (dRes.data && dRes.data.bs_actuals_meta) || {};
      setStored(st); setMeta(m); setLabels(overlayLeafLabels(m, gRes.data || [])); setDraft(draftOf(st));
      tell(st);
    } catch (e) {
      /* A FAILED READ IS NOT "NEVER ANSWERED". Nothing is shown as blank-and-required and the upload stays disabled. */
      setStored(null); setLoadError((e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'Unknown error');
      tell(null);
    } finally { setLoading(false); }
  }, [companyId, tell]);
  useEffect(() => { load(); }, [load]);

  const accountKeys = useMemo(() => accruedLeafKeysFromMeta(meta), [meta]);
  const labelOf = useCallback(k => (labels[k] && labels[k].label) || (meta[k] && meta[k].label) || k, [labels, meta]);

  const set = patch => { setDraft(d => ({ ...d, ...patch })); setError(''); };
  const biweekly = draft.frequency === 'biweekly';
  const accrues = biweekly && draft.books === 'accrues';

  // THE PREVIEW (Q12): only for an anchor + lag the rule accepts, from the engine's own calendar.
  const dateLagRefusal = useMemo(() => {
    if (!biweekly) return null;
    const v = validatePaySchedule({ pay_frequency: 'biweekly', pay_first_check_date: draft.firstCheck || null, pay_lag_days: lagValue(draft.lag), payroll_books: 'when_paid' });
    return v.refusal || null;
  }, [biweekly, draft.firstCheck, draft.lag]);
  const preview = useMemo(() => {
    if (!biweekly || dateLagRefusal) return [];
    return previewPayPeriods({ frequency: 'biweekly', firstCheckDate: draft.firstCheck, lagDays: lagValue(draft.lag) }, closeMonth, 6);
  }, [biweekly, dateLagRefusal, draft.firstCheck, draft.lag, closeMonth]);
  // a typed date / lag the rule refuses is said at once (a blank is not nagged before Save)
  const liveRefusal = dateLagRefusal && !/_REQUIRED$/.test(dateLagRefusal.code) ? dateLagRefusal.error : '';

  const dirty = stored ? JSON.stringify(draftOf(stored)) !== JSON.stringify(draft) : false;

  const save = async () => {
    if (saving) return;
    setError(''); setResult(null);
    const body = payScheduleBody(draft);
    const v = validatePaySchedule(body);
    if (v.refusal) { setError(v.refusal.error); return; }               // the server's own words, before any request
    if (accrues && !draft.account) { setError(ACCOUNT_REQUIRED_MESSAGE); return; }
    setSaving(true);
    try {
      const res = await savePaySchedule(companyId, body);
      const st = storedOf(res.data && res.data.pay_schedule);
      setStored(st); setDraft(draftOf(st));
      setResult({ relinked: (res.data && res.data.relinked) || {}, message: (res.data && res.data.roster_reimport_message) || '' });
      tell(st);
    } catch (e) {
      // a refusal: its message, and nothing changes -- the stored answer and the typed draft both stay as they were
      setError((e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'Unknown error');
    } finally { setSaving(false); }
  };

  const box = { border: '1px solid #E0E0E0', borderRadius: 8, padding: '18px 20px', marginBottom: 24, background: '#fff' };
  if (loading) return <div data-testid="pay-schedule-panel" style={{ ...box, color: '#999', fontFamily: FONT, fontSize: 13 }}>Loading the pay schedule...</div>;
  if (loadError) return (
    <div data-testid="pay-schedule-panel" style={box}>
      <div data-testid="pay-schedule-load-error" style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 10 }}>Could not read the pay schedule: {loadError}</div>
      <button type="button" onClick={load} style={{ ...field, minWidth: 0, cursor: 'pointer' }}>Retry</button>
    </div>
  );

  const storedSchedule = payScheduleFromSettings(stored);
  const storedAccount = stored && stored.accrued_payroll_bs_line_key;
  const rel = (result && result.relinked) || {};
  return (
    <div data-testid="pay-schedule-panel" style={box}>
      <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 6px' }}>Pay Schedule</h4>
      <div style={{ ...note, marginBottom: 16 }}>How this company pays its employees. Answer this before uploading the roster: the salary of every employee is calculated from it.</div>

      <div style={row}>
        <label style={label} htmlFor="pay-frequency">Pay frequency</label>
        <select id="pay-frequency" value={draft.frequency} onChange={e => set({ frequency: e.target.value })} style={field}>
          <option value="">Choose...</option>
          {PAY_FREQUENCIES.map(f => <option key={f} value={f}>{PAY_FREQUENCY_LABELS[f]}</option>)}
        </select>
      </div>

      {biweekly && <>
        <div style={row}>
          <label style={label} htmlFor="pay-first-check">First check date</label>
          <input id="pay-first-check" type="date" value={draft.firstCheck} onChange={e => set({ firstCheck: e.target.value })} style={{ ...field, fontFamily: MONO }} />
          <div style={hint}>{FIRST_CHECK_HINT}</div>
        </div>
        <div style={row}>
          <label style={label} htmlFor="pay-lag-days">Days from pay-period end to check date</label>
          <input id="pay-lag-days" type="text" inputMode="numeric" value={draft.lag} onChange={e => set({ lag: e.target.value })} style={{ ...field, minWidth: 80, width: 80, fontFamily: MONO }} />
          <div style={hint}>{PAY_LAG_DAYS_MIN} to {PAY_LAG_DAYS_MAX}</div>
        </div>
        {liveRefusal && <div data-testid="pay-schedule-live-refusal" style={{ color: '#B5543A', fontSize: 12, fontFamily: FONT, marginBottom: 14 }}>{liveRefusal}</div>}
        {preview.length > 0 && (
          <div data-testid="pay-schedule-preview" style={{ background: '#F0F6FF', borderRadius: 6, padding: '12px 14px', marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#1A1C2E', fontFamily: FONT, marginBottom: 6 }}>The next six pay periods{closeMonth ? ' after the ' + closeMonth + ' close' : ''}</div>
            {preview.map(p => <div key={p.k} data-testid="pay-schedule-preview-line" style={{ fontSize: 12, fontFamily: MONO, color: '#4A4E69', lineHeight: 1.7 }}>{payPeriodLine(p)}</div>)}
          </div>
        )}
        <div style={row}>
          <label style={label} htmlFor="payroll-books">Books</label>
          <select id="payroll-books" value={draft.books} onChange={e => set({ books: e.target.value })} style={field}>
            <option value="">Choose...</option>
            {PAYROLL_BOOKS.map(b => <option key={b} value={b}>{PAYROLL_BOOKS_LABELS[b]}</option>)}
          </select>
          {draft.books === 'when_paid' && <div data-testid="pay-schedule-when-paid" style={{ ...note, marginTop: 8 }}>{WHEN_PAID_STATEMENT}</div>}
        </div>
      </>}

      {accrues && <>
        <div style={row}>
          <label style={label} htmlFor="payroll-accrual-covers">The accrual covers</label>
          <select id="payroll-accrual-covers" value={draft.covers} onChange={e => set({ covers: e.target.value })} style={field}>
            <option value="">Choose...</option>
            {PAYROLL_ACCRUAL_COVERS.map(c => <option key={c} value={c}>{ACCRUAL_COVERS_LABELS[c]}</option>)}
          </select>
        </div>
        <div style={row}>
          <label style={label} htmlFor="accrued-payroll-account">Accrued-payroll account</label>
          {accountKeys.length === 0
            ? <div data-testid="pay-schedule-no-accounts" style={{ color: '#B5543A', fontSize: 13, fontFamily: FONT }}>{NO_ACCRUED_LINE_MESSAGE}</div>
            : <select id="accrued-payroll-account" value={draft.account} onChange={e => set({ account: e.target.value })} style={field}>
                <option value="">Choose...</option>
                {accountKeys.map(k => <option key={k} value={k}>{labelOf(k)}</option>)}
              </select>}
        </div>
      </>}

      {error && <div data-testid="pay-schedule-error" style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="button" onClick={save} disabled={saving} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#1A73E8', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: FONT, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.5 : 1 }}>
          {saving ? 'Saving...' : 'Save pay schedule'}
        </button>
        {dirty && !saving && <span data-testid="pay-schedule-unsaved" style={{ fontSize: 12, color: '#B5731A', fontFamily: FONT }}>Unsaved changes {'—'} Save to apply.</span>}
      </div>

      <div data-testid="pay-schedule-stored" style={{ ...note, marginTop: 14, paddingTop: 12, borderTop: '1px solid #F0F0F0' }}>
        <span style={{ fontWeight: 600 }}>Stored: </span>
        {stored && stored.pay_frequency ? payScheduleSummary(storedSchedule, storedAccount ? labelOf(storedAccount) : null) : 'not answered yet'}
        {stored && stored.payroll_books === 'when_paid' && <div style={{ marginTop: 6 }}>{WHEN_PAID_STATEMENT}</div>}
      </div>
      {result && (rel.linked || rel.unlinked || rel.created) && (
        <div data-testid="pay-schedule-relinked" style={{ ...note, marginTop: 8 }}>
          {rel.linked && <div>Linked: {labelOf(rel.linked)} now follows the payroll accrual in forecast months.</div>}
          {rel.created && rel.linked && <div>Created: {labelOf(rel.linked)} had no forecast assumption, so one was created for it.</div>}
          {rel.unlinked && <div>Unlinked: {labelOf(rel.unlinked)} is held at its own balance again (a constant).</div>}
        </div>
      )}
      {result && result.message && (
        <div data-testid="pay-schedule-reimport" style={{ marginTop: 10, padding: '10px 12px', borderRadius: 6, background: '#FFF8E1', border: '1px solid #F0D48A', color: '#7A5B00', fontSize: 13, fontFamily: FONT }}>{result.message}</div>
      )}
    </div>
  );
}
